import {
  CodexCategorySchema,
  CodexEntrySchema,
  CodexFieldSchema,
  CodexFieldType,
  NovelActSchema,
  NovelActTree,
  NovelChapterSchema,
  NovelSceneSchema,
  NovelSchema,
  NovelSeriesSchema,
  SnippetSchema,
} from '#/index';
import {
  CodexDetail,
  NovelLabel,
  OutlineTree,
  PointOfView,
} from '../../types';
import { getCodexEntryFields, getNovelActTree } from '../../utils';
import { FormatterContext, FormatterRenderer } from '..';
import { convertHtmlToFormattedText, convertJSONContentToFormattedText } from '@/utils';

type MarkdownRenderer<T> = FormatterRenderer<T>;

const joinSections = (sections: Array<string | undefined | null>, separator = '\n\n'): string =>
  sections.filter((section): section is string => Boolean(section && section.trim())).join(separator);

const withFallback = (value: string | undefined, fallback: string): string =>
  value && value.trim().length ? value : fallback;


const actTreeToMarkdown = (actTrees: NovelActTree[], mode: 'content' | 'summary'): string => {
  const lines: string[] = [];

  const formatAct = (act: NovelActTree, actIndex: number): void => {
    if (actIndex > 0) {
      lines.push(''); // 空行
      lines.push(`## ${act.fullName}`);
    }

    act.children?.forEach((chapter, chapterIndex) => {
      formatChapter(chapter, chapterIndex);
    });
  };

  const formatChapter = (chapter: NovelActTree, chapterIndex: number): void => {
    if (chapterIndex > 0) {
      lines.push(''); // 空行
    }
    lines.push(`### ${chapter.fullName}`);

    chapter.children?.forEach((scene, sceneIndex) => {
      formatScene(scene, sceneIndex);
    });
  };

  const formatScene = (scene: NovelActTree, sceneIndex: number): void => {
    if (sceneIndex > 0) {
      lines.push(''); // 空行
    }
    const sceneData = scene.data as NovelSceneSchema;

    // 构建场景标题：Chapter X - Scene Y
    let sceneLine = `#### ${scene.fullName}`;

    // 获取场景内容（summary 或 content）
    let sceneContent = '';
    if (mode === 'content' && sceneData.attributes.content) {
      sceneContent = convertJSONContentToFormattedText(sceneData.attributes.content);
    } else if (mode === 'summary' && sceneData.attributes.summary) {
      sceneContent = convertJSONContentToFormattedText(sceneData.attributes.summary);
    }

    lines.push(sceneLine);
    lines.push(sceneContent);
  };


  actTrees.forEach((act, actIndex) => {
    formatAct(act, actIndex);
  });
  return lines.join('\n');
}


const renderNovelMarkdown: MarkdownRenderer<NovelSchema> = novel => {
  const header = `# ${withFallback(novel.attributes.title, 'Untitled Novel')}`;
  const meta: string[] = [
    novel.attributes.author ? `- **Author:** ${novel.attributes.author}` : undefined,
    novel.attributes.language ? `- **Language:** ${novel.attributes.language}` : undefined,
    novel.attributes.tense ? `- **Tense:** ${novel.attributes.tense}` : undefined,
  ].filter(Boolean) as string[];

  if (novel.attributes.pov) {
    meta.push(
      `- **POV:** ${novel.attributes.pov.type}${novel.attributes.pov.characterId ? ` (${novel.attributes.pov.characterId})` : ''
      }`
    );
  }
  if (novel.attributes.labels?.length) {
    meta.push(`- **Labels:** ${novel.attributes.labels.map(label => label.label).join(', ')}`);
  }

  return joinSections([header, meta.join('\n')]);
};

const renderSeriesMarkdown: MarkdownRenderer<NovelSeriesSchema> = series =>
  joinSections([
    `# Series: ${withFallback(series.attributes.title, 'Untitled Series')}`,
    series.attributes.description ? series.attributes.description.trim() : undefined,
  ]);

const renderActMarkdown: MarkdownRenderer<NovelActSchema> = (act, ctx) => {
  const tree = getNovelActTree(ctx.globalContext.generateStore.getState().novelActsTree, { actIds: [act.id] })
  return actTreeToMarkdown(tree, 'content');
};

const renderChapterMarkdown: MarkdownRenderer<NovelChapterSchema> = (chapter, ctx) => {
  const tree = getNovelActTree(ctx.globalContext.generateStore.getState().novelActsTree, { chapterIds: [chapter.id] })
  return actTreeToMarkdown(tree, 'content');
};

const renderSceneMarkdown: MarkdownRenderer<NovelSceneSchema> = (scene, ctx) => {
  const tree = getNovelActTree(ctx.globalContext.generateStore.getState().novelActsTree, { sceneIds: [scene.id] })

  return actTreeToMarkdown(tree, 'content');
};

const renderCodexEntryMarkdown: MarkdownRenderer<CodexEntrySchema> = (entry, ctx) => {
  const aliases = entry.attributes.aliases ? ` (${entry.attributes.aliases})` : ''
  const title = `${entry.attributes.name}${aliases}`
  let textList = getCodexEntryFields(ctx, entry)
    .filter(d => d.type === CodexFieldType.Text)
    .map(d => d.value);

  if (entry.attributes.description) {
    textList.unshift(entry.attributes.description)
  }

  textList = textList.map(d => convertHtmlToFormattedText(d));

  return joinSections([`## ${title}`, ...textList])
};

const renderCodexCategoryMarkdown: MarkdownRenderer<CodexCategorySchema> = category =>
  `## Codex Category: ${withFallback(category.attributes.name, 'Unnamed Category')}`;

const renderCodexDetailMarkdown: MarkdownRenderer<CodexDetail> = detail =>
  joinSections([
    `## Codex Detail: ${withFallback(detail.entry.attributes.name, detail.entry.id)}`,
    `- **Type:** ${detail.entry.attributes.type}`,
    detail.fields.map(field => `- **${field.name}:** ${field.value}`).join('\n'),
  ]);

const renderSnippetMarkdown: MarkdownRenderer<SnippetSchema> = snippet =>
  joinSections([
    `## Snippet: ${withFallback(snippet.attributes.title, 'Untitled Snippet')}`,
    snippet.attributes.content ? convertJSONContentToFormattedText(snippet.attributes.content) : undefined,
  ]);

const renderOutlineTreeMarkdown: MarkdownRenderer<OutlineTree> = outline => {
  if (!outline.data?.length) return '';
  return actTreeToMarkdown(outline.data, outline.mode);
};

const renderLabelMarkdown: MarkdownRenderer<NovelLabel> = label =>
  `- **Label:** ${label.data.label}${label.data.color ? ` (${label.data.color})` : ''}`;

const renderPointOfViewMarkdown: MarkdownRenderer<PointOfView> = pov =>
  `- **POV:** ${pov.value}${pov.characterId ? ` (${pov.characterId})` : ''}`;



export const markdownRenderers = {
  renderNovelMarkdown,
  renderSeriesMarkdown,
  renderActMarkdown,
  renderChapterMarkdown,
  renderSceneMarkdown,
  renderCodexEntryMarkdown,
  renderCodexCategoryMarkdown,
  renderCodexDetailMarkdown,
  renderSnippetMarkdown,
  renderOutlineTreeMarkdown,
  renderLabelMarkdown,
  renderPointOfViewMarkdown,
}